import { useSelector } from '@xstate/store-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  readCachedFilterState,
  writeCachedFilterState,
} from './filterStateCache';
import {
  shouldSyncSavedFilterSelection,
  syncSavedFilterSelection,
} from './savedFilterSelectionSync';
import {
  createUniversalFiltersStore,
  DEFAULT_PRIMARY_PREDEFINED_DATE_RANGE,
} from './store';
import {
  type UniversalFiltersContextValue,
  type UniversalFiltersProviderProps,
  type UniversalFiltersSavedFiltersQueryState,
} from './UniversalFiltersProvider.types';

const emptySavedFiltersState: UniversalFiltersSavedFiltersQueryState = {
  error: null,
  filters: [],
  isFetching: false,
  isLoading: false,
};

const UniversalFiltersContext = createContext<
  UniversalFiltersContextValue<unknown> | undefined
>(undefined);

/**
 * Provides one isolated universal-filter store and optional saved-filter
 * integration for a page subtree.
 *
 * Standard UI pieces are composable: consumers may mount only the primary row,
 * only a custom UI, an ephemeral drawer, or the complete saved-filter suite.
 */
export function UniversalFiltersProvider<TPayload>({
  children,
  config,
  savedFilterUrlSync,
  savedFiltersState = emptySavedFiltersState,
}: UniversalFiltersProviderProps<TPayload>) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const persistenceKey = config.persistenceKey ?? config.type;
  const [store] = useState(() =>
    createUniversalFiltersStore({
      defaultFilterClauses: config.defaultFilters,
      initialContext: readCachedFilterState(persistenceKey),
      primaryDateDefaultPredefinedRange: config.dateField
        ? (config.dateField.defaultPredefinedRange ??
          DEFAULT_PRIMARY_PREDEFINED_DATE_RANGE)
        : undefined,
    }),
  );
  const selectedFilterId = useSelector(
    store,
    (snapshot) => snapshot.context.selectedFilterId,
  );
  const hasSavedFiltersEnabled = Boolean(config.savedFilters?.enabled);
  const isSavedFiltersLoading = savedFiltersState.isLoading;
  const routeSavedFilterId = savedFilterUrlSync?.savedFilterId;
  const savedFilters = savedFiltersState.filters;
  const previousSelectedFilterIdRef = useRef(selectedFilterId);

  useEffect(() => {
    const subscription = store.subscribe((snapshot) => {
      writeCachedFilterState(persistenceKey, snapshot.context);
    });

    return () => subscription.unsubscribe();
  }, [persistenceKey, store]);

  useEffect(() => {
    store.trigger.savedFiltersChanged({
      savedFilters: savedFilters.map(({ filters, id }) => ({
        filters,
        id,
      })),
    });
  }, [savedFilters, store]);

  useEffect(() => {
    if (
      !shouldSyncSavedFilterSelection(
        hasSavedFiltersEnabled,
        savedFilterUrlSync,
      )
    ) {
      return;
    }

    syncSavedFilterSelection({
      isSavedFiltersLoading,
      previousSelectedFilterIdRef,
      routeSavedFilterId,
      savedFilters,
      savedFilterUrlSync,
      selectedFilterId,
      store,
    });
  }, [
    hasSavedFiltersEnabled,
    isSavedFiltersLoading,
    routeSavedFilterId,
    savedFilters,
    savedFilterUrlSync,
    selectedFilterId,
    store,
  ]);

  const selectedFilterMeta = useMemo(() => {
    const selectedFilter = savedFiltersState.filters.find(
      (filter) => filter.id === selectedFilterId,
    );

    if (!selectedFilter) return undefined;

    return {
      id: selectedFilter.id,
      name: selectedFilter.name,
      type: selectedFilter.type,
      version: selectedFilter.version,
    };
  }, [savedFiltersState.filters, selectedFilterId]);

  const savedFiltersViewState = useMemo(
    () => ({
      ...savedFiltersState,
      selectedFilterMeta,
    }),
    [savedFiltersState, selectedFilterMeta],
  );
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);
  const contextValue = useMemo(
    () => ({
      closeDrawer,
      config,
      isDrawerOpen,
      openDrawer,
      savedFiltersState: savedFiltersViewState,
      store,
    }),
    [
      closeDrawer,
      config,
      isDrawerOpen,
      openDrawer,
      savedFiltersViewState,
      store,
    ],
  );

  return (
    <UniversalFiltersContext.Provider value={contextValue}>
      {children}
    </UniversalFiltersContext.Provider>
  );
}

/**
 * Returns the provider-scoped universal-filter context.
 *
 * @throws When called outside `UniversalFiltersProvider`.
 */
export function useUniversalFiltersContext<
  TPayload = unknown,
>(): UniversalFiltersContextValue<TPayload> {
  const context = useContext(UniversalFiltersContext);

  if (context === undefined) {
    throw new Error(
      'useUniversalFiltersContext must be used within a UniversalFiltersProvider',
    );
  }

  return context;
}
